import type { BillingProduct } from './dto.ts';

/**
 * The arithmetic and the plan rules behind the billing screens, kept out of
 * the components so that they can be tested without rendering anything.
 *
 * **The server is the authority on every rule here.** `PUT /tenants/:id/plan`
 * validates the combination again and refuses what it will not bill, and the
 * total an invoice carries is Stripe's. This module exists so that an owner is
 * never offered a combination the server will refuse, and so that the price
 * beside a tick box moves as the box is ticked rather than after a round trip.
 */

export const BASE_PRODUCT_ID = 'base';
export const SUITE_PRODUCT_ID = 'suite';

/**
 * The framework the base includes. The id is the catalogue's; there is no
 * product for it, because nobody can buy a client without it.
 */
export const INCLUDED_FRAMEWORK_ID = 'CIS_M365';

/** Assessments a month the base includes, before any scan pack. */
export const INCLUDED_SCANS = 5;

/*
 * `Intl` rather than `toFixed` with a pound sign glued on: it groups
 * thousands ("£1,210.00"), which an account with forty clients will reach.
 */
const GBP = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** 2900 → "£29.00". Pence in, a string out, and nothing in between is a float that gets added to. */
export const formatPence = (pence: number): string => GBP.format(pence / 100);

const byId = (products: readonly BillingProduct[], id: string): BillingProduct | undefined =>
  products.find((product) => product.id === id);

const kindOf = (products: readonly BillingProduct[], id: string) => byId(products, id)?.kind;

/**
 * Ids in the catalogue's order, unknown ids last. The server does not care
 * about order, but a plan that is the same set in a different order must not
 * look "changed" to a Save button, and the lines on a bill should not shuffle.
 */
export const orderedIds = (
  productIds: readonly string[],
  products: readonly BillingProduct[],
): string[] => {
  const unique = [...new Set(productIds)];
  const known = products.map((product) => product.id).filter((id) => unique.includes(id));
  const unknown = unique.filter((id) => byId(products, id) === undefined);
  return [...known, ...unknown];
};

/**
 * The base is not optional and cannot be doubled. Applied to whatever the
 * server last said, so a plan read from an older server that sent none still
 * renders — and saves — as a billable client.
 */
export const withBase = (productIds: readonly string[]): string[] => [
  BASE_PRODUCT_ID,
  ...productIds.filter((id) => id !== BASE_PRODUCT_ID),
];

/**
 * Tick or untick one framework. Ticking one while the suite is chosen leaves
 * the suite: a framework bought twice — once on its own and once inside the
 * suite — is the one combination the server refuses that an owner could reach
 * by clicking.
 */
export const toggleFramework = (
  productIds: readonly string[],
  frameworkProductId: string,
  products: readonly BillingProduct[],
): string[] =>
  productIds.includes(frameworkProductId)
    ? productIds.filter((id) => id !== frameworkProductId)
    : [...productIds.filter((id) => kindOf(products, id) !== 'suite'), frameworkProductId];

/** Choose or drop the full suite. Choosing it clears every framework bought one by one. */
export const toggleSuite = (
  productIds: readonly string[],
  products: readonly BillingProduct[],
): string[] =>
  productIds.includes(SUITE_PRODUCT_ID)
    ? productIds.filter((id) => id !== SUITE_PRODUCT_ID)
    : [
        ...productIds.filter((id) => kindOf(products, id) !== 'framework'),
        SUITE_PRODUCT_ID,
      ];

/** Replace the scan pack. Null is the base's included allowance, and no pack at all. */
export const setScanPack = (
  productIds: readonly string[],
  packId: string | null,
  products: readonly BillingProduct[],
): string[] => [
  ...productIds.filter((id) => kindOf(products, id) !== 'scans'),
  ...(packId === null ? [] : [packId]),
];

/** The chosen scan pack's id, or null when the client is on the included allowance. */
export const scanPackOf = (
  productIds: readonly string[],
  products: readonly BillingProduct[],
): string | null => productIds.find((id) => kindOf(products, id) === 'scans') ?? null;

/**
 * Why the server would refuse this plan, as sentences, or an empty list.
 *
 * The same three rules `packages/billing` enforces, plus the one the console
 * can be wrong about on its own: an id the catalogue it was handed does not
 * contain, which is a retired product or a stale page.
 */
export const planProblems = (
  productIds: readonly string[],
  products: readonly BillingProduct[],
): string[] => {
  const problems: string[] = [];
  const count = (kind: BillingProduct['kind']) =>
    productIds.filter((id) => kindOf(products, id) === kind).length;

  const unknown = productIds.filter((id) => byId(products, id) === undefined);
  if (unknown.length > 0) {
    problems.push(`Not on sale: ${unknown.join(', ')}.`);
  }
  if (count('base') !== 1) {
    problems.push('Every client has exactly one base plan.');
  }
  if (count('suite') > 0 && count('framework') > 0) {
    problems.push(
      'The full framework suite already includes every framework, so none can be added on its own as well.',
    );
  }
  if (count('suite') > 1) {
    problems.push('The full framework suite can only be chosen once.');
  }
  if (count('scans') > 1) {
    problems.push('A client can have at most one scan pack.');
  }
  return problems;
};

/** Per client, per month, ex-VAT, in pence. An id the catalogue does not know costs nothing here. */
export const planTotal = (
  productIds: readonly string[],
  products: readonly BillingProduct[],
): number =>
  productIds.reduce((sum, id) => sum + (byId(products, id)?.pricePence ?? 0), 0);

/**
 * The short names the scan-pack choice is offered under. The product's own
 * name is the fallback, so a pack added in the platform area is still labelled
 * rather than hidden.
 */
const SCAN_PACK_SHORT: Readonly<Record<string, string>> = {
  'scans-plus': 'Plus',
  'scans-daily': 'Daily',
};

/** '5 a month, included' / 'Plus — 15 a month' / 'Daily — 31 a month'. */
export const scanLabel = (pack: BillingProduct | null): string =>
  pack === null
    ? `${INCLUDED_SCANS} a month, included`
    : `${SCAN_PACK_SHORT[pack.id] ?? pack.name} — ${pack.scansPerPeriod ?? INCLUDED_SCANS} a month`;

/** The products on a plan, as names, in the catalogue's order. An unknown id is shown as itself. */
export const planLines = (
  productIds: readonly string[],
  products: readonly BillingProduct[],
): string[] => orderedIds(productIds, products).map((id) => byId(products, id)?.name ?? id);

/**
 * Whether a client's plan lets its report cover a framework. Null is an
 * unrestricted client, which is every client until billing is switched on.
 */
export const entitledTo = (frameworks: readonly string[] | null, frameworkId: string): boolean =>
  frameworks === null || frameworks.includes(frameworkId);

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whole days left in a trial, rounded up so that the last afternoon reads as
 * "1 day" rather than "0 days" while it is still usable. Never negative, and
 * null for a date that does not parse. `now` is passed in, so this is testable
 * on any day of the year.
 */
export const trialDaysLeft = (trialEndsAt: string, now: Date): number | null => {
  const end = Date.parse(trialEndsAt);
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - now.getTime()) / DAY_MS));
};

/**
 * "29", "29.5" and "29.50" → 2950 pence. Integer arithmetic on the two halves,
 * so "0.29" is 29 and never 28.999…; anything else — a pound sign, a comma, a
 * third decimal place, a negative — is null, and the editor refuses to save it.
 */
export const poundsToPence = (text: string): number | null => {
  const match = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(text.trim());
  if (match === null) return null;
  const pounds = Number(match[1]);
  const pence = Number((match[2] ?? '').padEnd(2, '0'));
  return pounds * 100 + pence;
};

/** 2950 → "29.50": the value an editable price field starts with. */
export const penceToPounds = (pence: number): string => (pence / 100).toFixed(2);
